import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMessage } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Header } from "@/components/Header";
import { AudioPlayer } from "@/components/AudioPlayer";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Wifi, CircleDot, Instagram, Music, Flag, ArrowLeft, MessageSquare, Pencil, Bookmark } from "lucide-react";

export default function ProfileView() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  const isMine = user && user.user_id === userId;

  useEffect(() => {
    setLoading(true);
    api.get(`/profiles/${userId}`)
      .then((r) => { setP(r.data); })
      .catch((e) => { if (e?.response?.status === 402) navigate("/paywall"); else navigate("/directory"); })
      .finally(() => setLoading(false));
  }, [userId, navigate]);

  useEffect(() => {
    if (user?.has_browse_access) api.get("/saved").then((r) => setSaved((r.data.ids || []).includes(userId))).catch(() => {});
  }, [user, userId]);

  if (loading) return <div className="min-h-screen bg-ink flex items-center justify-center"><span className="font-mono text-xs uppercase tracking-[0.3em] text-ash animate-pulse">Loading…</span></div>;
  if (!p) return null;

  const avatar = mediaUrl(p.avatar);

  const toggleSave = async () => {
    try {
      if (saved) { await api.delete(`/saved/${userId}`); setSaved(false); toast("Removed from your playlist"); }
      else { await api.post(`/saved/${userId}`); setSaved(true); toast.success("Saved to your playlist"); }
    } catch (e) { toast.error(errMessage(e)); }
  };

  const linkHref = (l) => {
    const v = (p.links[l.k] || "").trim();
    if (/^https?:\/\//i.test(v)) return v;                    // full URL pasted — use as-is
    if (/^(www\.|open\.spotify|instagram\.com|tiktok\.com)/i.test(v)) return `https://${v}`;
    return l.base + v.replace(/^@/, "");                       // bare handle
  };

  const links = [
    { k: "instagram", icon: Instagram, base: "https://instagram.com/" },
    { k: "spotify", icon: Music, base: "https://open.spotify.com/search/" },
    { k: "tiktok", icon: Music, base: "https://tiktok.com/@" },
  ].filter((l) => p.links?.[l.k]);

  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-24 pb-20">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-ash hover:text-gold transition-colors mb-8"><ArrowLeft size={14} /> Back</button>

        <div className="grid lg:grid-cols-[360px_1fr] gap-px bg-line border border-line">
          {/* Left */}
          <div className="bg-surface p-7 flex flex-col gap-6">
            <div className="aspect-square bg-ink border border-line overflow-hidden">
              {avatar ? <img src={avatar} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
            </div>
            <div>
              <h1 className="font-heading text-4xl font-bold tracking-tighter uppercase text-cream leading-none">{p.name}</h1>
            </div>
            <div className="flex flex-col gap-2 font-mono text-xs uppercase tracking-wide text-ash">
              {p.location && <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-gold" /> {p.location}</span>}
              {p.remote_friendly && <span className="inline-flex items-center gap-2"><Wifi size={14} className="text-gold" /> Remote friendly</span>}
              <span className="inline-flex items-center gap-2"><CircleDot size={14} className="text-gold" /> {p.open_to_collaborate ? "Open to collaborate" : "Not currently open"}</span>
            </div>

            {links.length > 0 && (
              <div className="flex gap-3">
                {links.map((l) => (
                  <a key={l.k} href={linkHref(l)} target="_blank" rel="noreferrer" data-testid={`link-${l.k}`} className="w-10 h-10 flex items-center justify-center border border-line text-ash hover:border-gold hover:text-gold transition-colors">
                    <l.icon size={16} />
                  </a>
                ))}
              </div>
            )}

            {isMine ? (
              <button onClick={() => navigate("/profile")} data-testid="edit-my-profile-btn" className="inline-flex items-center justify-center gap-2 border border-gold text-gold font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-gold hover:text-ink transition-colors"><Pencil size={15} /> Edit my HausCard</button>
            ) : (
              <div className="flex flex-col gap-3">
                {p.dms_enabled === false ? (
                  <div data-testid="messages-off-note" className="flex items-center justify-center gap-2 border border-dashed border-line text-ash font-mono text-[11px] uppercase tracking-widest px-6 py-3.5"><MessageSquare size={14} /> Messages turned off</div>
                ) : (
                  <button onClick={() => navigate(`/messages?to=${userId}`)} data-testid="message-writer-btn" className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-gold-hi transition-colors"><MessageSquare size={15} /> Message</button>
                )}
                <button onClick={toggleSave} data-testid="save-writer-btn" className={`inline-flex items-center justify-center gap-2 border font-bold uppercase tracking-widest text-xs px-6 py-3.5 transition-colors ${saved ? "border-gold bg-gold/10 text-gold" : "border-line text-cream hover:border-gold hover:text-gold"}`}><Bookmark size={15} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved to playlist" : "Save to playlist"}</button>
              </div>
            )}
          </div>

          {/* Right */}
          <div className="bg-surface p-7 md:p-9 flex flex-col gap-8">
            {p.intro_video && (
              <div>
                <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-4">Intro</h2>
                <video src={mediaUrl(p.intro_video)} controls playsInline data-testid="profile-intro-video" className="w-full aspect-video bg-ink border border-line" />
              </div>
            )}
            {p.bio && <p className="text-lg text-cream leading-relaxed font-light">{p.bio}</p>}

            <div className="flex flex-wrap gap-2">
              {(p.roles || []).map((r) => <span key={r} className="border border-line px-3 py-1 font-mono text-[11px] uppercase text-ash">{r}</span>)}
            </div>
            <div className="flex flex-wrap gap-2">
              {(p.genres || []).map((g) => <span key={g} className="font-mono text-[11px] uppercase tracking-wider text-gold/80">{g}</span>)}
            </div>

            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-4">Songs ({p.songs?.length || 0})</h2>
              {p.songs?.length ? (
                <div className="flex flex-col gap-3">{p.songs.map((s, i) => <AudioPlayer key={s.id} song={s} index={i} />)}</div>
              ) : <p className="text-ash text-sm">No songs uploaded yet.</p>}
            </div>

            {p.gallery?.length > 0 && (
              <div>
                <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-4">Gallery</h2>
                <div className="grid grid-cols-2 gap-3">
                  {p.gallery.map((g) => <div key={g.id} className="aspect-square bg-ink border border-line overflow-hidden"><img src={mediaUrl(g.media)} alt="" className="w-full h-full object-cover media-grayscale" /></div>)}
                </div>
              </div>
            )}

            {!isMine && (
              <button onClick={() => toast("Reported. Our moderation queue will review this profile.")} data-testid="report-profile-btn" className="self-start inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-ash hover:text-destructive transition-colors"><Flag size={13} /> Report this profile</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
